import React, { useState, useEffect, useMemo, useRef, useCallback } from "react";
import api from "../../services/api";
import "../../assets/styles/HR/AuditPg.css";

/* ── helpers ───────────────────────────────────────────────── */
const monthKey  = (d) => { try { const dt=new Date(d); return `${dt.getFullYear()}-${String(dt.getMonth()+1).padStart(2,"0")}`; } catch{return null;} };
const dayKey    = (d) => { try { return new Date(d).toISOString().split("T")[0]; } catch{return null;} };
const todayKey  = () => new Date().toISOString().split("T")[0];
const fmtDate   = (d) => { try { return new Date(d).toLocaleDateString("en-IN",{day:"2-digit",month:"short",year:"numeric"}); } catch{return d||"—";} };
const monthLabel= (k) => { if(!k)return""; const [y,m]=k.split("-"); return new Date(Number(y),Number(m)-1,1).toLocaleDateString("en-IN",{month:"long",year:"numeric"}); };

/* ── CSV export ─────────────────────────────────────────────── */
function exportCSV(rows, filename) {
  const headers = ["Timestamp","User","Role","Action","Detail","Date"];
  const esc = (v) => `"${String(v||"").replace(/"/g,'""')}"`;
  const lines = [
    headers.join(","),
    ...rows.map(l => [
      l.timestamp_str||"",
      l.user_name||"",
      l.role||"",
      l.action||"",
      l.detail||"",
      l.created_at ? new Date(l.created_at).toLocaleDateString("en-IN") : (l.timestamp_str||"")
    ].map(esc).join(","))
  ];
  const blob = new Blob([lines.join("\n")], {type:"text/csv;charset=utf-8;"});
  const url  = URL.createObjectURL(blob);
  const a    = document.createElement("a"); a.href=url; a.download=filename; a.click();
  URL.revokeObjectURL(url);
}

/* ── action chip colour ─────────────────────────────────────── */
const actionColor = (action="") => {
  const a = action.toLowerCase();
  if (a.includes("delete")||a.includes("reject")||a.includes("inactive"))
    return {bg:"#fef2f2", color:"#dc2626"};
  if (a.includes("creat")||a.includes("verif")||a.includes("approv")||a.includes("complet"))
    return {bg:"#eff6ff", color:"#2563eb"};
  if (a.includes("update")||a.includes("edit")||a.includes("change")||a.includes("salary"))
    return {bg:"#fef3c7", color:"#d97706"};
  if (a.includes("override")||a.includes("leave"))
    return {bg:"#ecfdf5", color:"#059669"};
  return {bg:"#f3f4f6", color:"#374151"};
};

/* ─────────────────────────────────────────────────────────────── */
function AuditPg() {
  const [logs,       setLogs]       = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [search,     setSearch]     = useState("");
  const [actionF,    setActionF]    = useState("all");
  const [dayFilter,  setDayFilter]  = useState("all");
  const [monthF,     setMonthF]     = useState("all");
  const [showMDrop,  setShowMDrop]  = useState(false);
  const mDropRef = useRef(null);

  /* close monthly dropdown on outside click */
  useEffect(() => {
    const h = (e) => { if (mDropRef.current && !mDropRef.current.contains(e.target)) setShowMDrop(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  /* load ALL logs once */
  useEffect(() => {
    setLoading(true);
    fetch(`${process.env.REACT_APP_API_URL||"/api"}/hr/audit-logs`, {
      headers: { Authorization: `Bearer ${api.getToken()}` }
    })
    .then(r=>r.json())
    .then(d=>{ setLogs(Array.isArray(d)?d:[]); setLoading(false); })
    .catch(()=>setLoading(false));
  }, []);

  /* derived options */
  const availMonths = useMemo(()=>{
    const s=new Set(); logs.forEach(l=>{ const k=monthKey(l.created_at||l.timestamp_str); if(k) s.add(k); });
    return [...s].sort((a,b)=>b.localeCompare(a));
  },[logs]);

  const availDays = useMemo(()=>{
    const s=new Set(); logs.forEach(l=>{ const k=dayKey(l.created_at); if(k) s.add(k); });
    return [...s].sort((a,b)=>b.localeCompare(a)).slice(0,30);
  },[logs]);

  const availActions = useMemo(()=>{
    const s=new Set(); logs.forEach(l=>{ if(l.action) s.add(l.action); });
    return [...s].sort();
  },[logs]);

  /* filtered list */
  const filtered = useMemo(()=>{
    let list = logs;
    if (dayFilter==="today")   list = list.filter(l=>dayKey(l.created_at)===todayKey());
    else if (dayFilter!=="all") list = list.filter(l=>dayKey(l.created_at)===dayFilter);
    if (monthF!=="all")         list = list.filter(l=>monthKey(l.created_at||l.timestamp_str)===monthF);
    if (actionF!=="all")        list = list.filter(l=>(l.action||"").toLowerCase().includes(actionF.toLowerCase()));
    if (search.trim()) {
      const q=search.trim().toLowerCase();
      list = list.filter(l=>
        (l.user_name||"").toLowerCase().includes(q)||
        (l.action||"").toLowerCase().includes(q)||
        (l.detail||"").toLowerCase().includes(q)||
        (l.role||"").toLowerCase().includes(q)
      );
    }
    return list;
  },[logs,dayFilter,monthF,actionF,search]);

  /* stats */
  const todayCount   = useMemo(()=>logs.filter(l=>dayKey(l.created_at)===todayKey()).length,[logs]);
  const thisMonthCnt = useMemo(()=>logs.filter(l=>monthKey(l.created_at||l.timestamp_str)===monthKey(new Date().toISOString())).length,[logs]);

  const anyFilter = dayFilter!=="all"||monthF!=="all"||actionF!=="all"||search.trim();

  const csvName = ()=>{
    const parts=["hr_audit_logs"];
    if(dayFilter==="today") parts.push("today_"+todayKey());
    else if(dayFilter!=="all") parts.push(dayFilter);
    if(monthF!=="all") parts.push(monthF);
    if(actionF!=="all") parts.push(actionF.replace(/\s+/g,"_").toLowerCase());
    return parts.join("_")+".csv";
  };

  const resetAll = ()=>{ setSearch(""); setActionF("all"); setDayFilter("all"); setMonthF("all"); };

  const SS = { padding:"8px 12px", border:"1px solid #e2e8f0", borderRadius:8,
    fontSize:12, color:"#374151", background:"#fff", cursor:"pointer", outline:"none" };

  return (
    <div>
      <div className="sec">Audit Logs</div>

      {/* ── toolbar ── */}
      <div style={{display:"flex",gap:10,flexWrap:"wrap",marginBottom:14,alignItems:"center"}}>

        {/* search */}
        <input
          placeholder="🔍 Search user, action, detail…"
          value={search} onChange={e=>setSearch(e.target.value)}
          style={{...SS, flex:1, minWidth:180}}
        />

        {/* day filter */}
        <select value={dayFilter} onChange={e=>setDayFilter(e.target.value)} style={SS}>
          <option value="all">All Days</option>
          <option value="today">Today</option>
          {availDays.filter(d=>d!==todayKey()).map(d=>(
            <option key={d} value={d}>{fmtDate(d)}</option>
          ))}
        </select>

        {/* month filter */}
        <select value={monthF} onChange={e=>setMonthF(e.target.value)} style={SS}>
          <option value="all">All Months</option>
          {availMonths.map(m=>(
            <option key={m} value={m}>{monthLabel(m)}</option>
          ))}
        </select>

        {/* action filter */}
        <select value={actionF} onChange={e=>setActionF(e.target.value)} style={SS}>
          <option value="all">All Actions</option>
          {availActions.map(a=>(
            <option key={a} value={a}>{a}</option>
          ))}
        </select>

        {/* reset */}
        {anyFilter && (
          <button onClick={resetAll} style={{
            padding:"7px 12px", border:"1px solid #fecaca",
            borderRadius:8, background:"#fef2f2", color:"#ef4444",
            fontSize:11, fontWeight:700, cursor:"pointer"
          }}>
            ✕ Reset
          </button>
        )}

        {/* Monthly CSV dropdown */}
        <div ref={mDropRef} style={{position:"relative"}}>
          <button
            onClick={()=>setShowMDrop(v=>!v)}
            style={{display:"flex",alignItems:"center",gap:5,padding:"8px 14px",
              background:"#fff",border:"1px solid #e2e8f0",borderRadius:8,
              fontWeight:600,fontSize:12,color:"#374151",cursor:"pointer"}}>
            📅 Monthly CSV ▾
          </button>
          {showMDrop && (
            <div style={{
              position:"absolute",right:0,top:"calc(100% + 6px)",
              background:"#fff",border:"1px solid #e2e8f0",borderRadius:10,
              boxShadow:"0 8px 24px rgba(0,0,0,.12)",zIndex:200,
              minWidth:240,overflow:"hidden"
            }}>
              <div style={{padding:"8px 12px",fontSize:11,fontWeight:700,color:"#64748b",
                borderBottom:"1px solid #f1f5f9",background:"#f8fafc"}}>SELECT MONTH</div>

              {/* Today shortcut */}
              <button
                onClick={()=>{ const t=logs.filter(l=>dayKey(l.created_at)===todayKey()); exportCSV(t,`hr_audit_today_${todayKey()}.csv`); setShowMDrop(false); }}
                style={{display:"flex",justifyContent:"space-between",width:"100%",
                  padding:"10px 14px",border:"none",background:"#fff",cursor:"pointer",
                  fontSize:13,borderBottom:"1px solid #f8fafc"}}
                onMouseEnter={e=>e.currentTarget.style.background="#f0f9ff"}
                onMouseLeave={e=>e.currentTarget.style.background="#fff"}>
                <span style={{fontWeight:700,color:"#059669"}}>📅 Today</span>
                <span style={{fontSize:11,color:"#94a3b8"}}>{todayCount} logs</span>
              </button>

              {availMonths.map(m=>{
                const mRows=logs.filter(l=>monthKey(l.created_at||l.timestamp_str)===m);
                return (
                  <button key={m}
                    onClick={()=>{ exportCSV(mRows,`hr_audit_${m}.csv`); setShowMDrop(false); }}
                    style={{display:"flex",justifyContent:"space-between",alignItems:"center",
                      width:"100%",padding:"10px 14px",border:"none",background:"#fff",
                      cursor:"pointer",fontSize:13,borderBottom:"1px solid #f8fafc"}}
                    onMouseEnter={e=>e.currentTarget.style.background="#f0f9ff"}
                    onMouseLeave={e=>e.currentTarget.style.background="#fff"}>
                    <span style={{fontWeight:600,color:"#1e293b"}}>{monthLabel(m)}</span>
                    <span style={{fontSize:11,color:"#94a3b8"}}>{mRows.length} logs</span>
                  </button>
                );
              })}

              {availMonths.length===0 && (
                <div style={{padding:"14px",fontSize:12,color:"#94a3b8",textAlign:"center"}}>No monthly data yet</div>
              )}
            </div>
          )}
        </div>

        {/* Download current view */}
        <button
          onClick={()=>exportCSV(filtered,csvName())}
          disabled={filtered.length===0}
          style={{display:"flex",alignItems:"center",gap:6,padding:"8px 14px",
            background: filtered.length===0 ? "#f1f5f9" : "#0f172a",
            color:       filtered.length===0 ? "#94a3b8" : "#fff",
            border:"none",borderRadius:8,fontWeight:700,fontSize:12,
            cursor: filtered.length===0 ? "not-allowed" : "pointer",
            whiteSpace:"nowrap"}}>
          ⬇ Download CSV{filtered.length!==logs.length ? ` (${filtered.length})` : ""}
        </button>
      </div>

      {/* ── active filter banner ── */}
      {(dayFilter!=="all"||monthF!=="all") && (
        <div style={{background:"linear-gradient(135deg,#1e1b4b,#4f46e5)",borderRadius:10,
          padding:"10px 16px",marginBottom:14,display:"flex",
          justifyContent:"space-between",alignItems:"center",color:"#fff"}}>
          <span style={{fontSize:12,fontWeight:600}}>
            {dayFilter==="today" ? "📅 Today's Logs"
              : dayFilter!=="all" ? `📅 ${fmtDate(dayFilter)}`
              : `🗓️ ${monthLabel(monthF)}`}
            {" "}— {filtered.length} record{filtered.length!==1?"s":""}
          </span>
          <button onClick={resetAll}
            style={{background:"rgba(255,255,255,.15)",border:"1px solid rgba(255,255,255,.25)",
              color:"#fff",borderRadius:6,padding:"4px 10px",fontSize:11,
              fontWeight:600,cursor:"pointer"}}>
            Clear
          </button>
        </div>
      )}

      {/* ── table ── */}
      <div className="cd">
        <div className="cd-h">
          <div className="cd-t">Activity Trail</div>
          <div className="cd-s">
            {anyFilter ? `${filtered.length} of ${logs.length} logs` : `${logs.length} logs`}
          </div>
        </div>
        <div className="cd-b" style={{padding:0,overflowX:"auto"}}>
          {loading ? (
            <div style={{textAlign:"center",padding:30,color:"#94a3b8",fontSize:13}}>Loading...</div>
          ) : filtered.length===0 ? (
            <div style={{textAlign:"center",padding:30,color:"#94a3b8",fontSize:13}}>
              {anyFilter ? "No logs match your filters." : "No logs found"}
            </div>
          ) : (
            <table className="tbl">
              <thead>
                <tr>
                  <th>Timestamp</th>
                  <th>User</th>
                  <th>Role</th>
                  <th>Action</th>
                  <th>Detail</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((l, i) => {
                  const ac = actionColor(l.action);
                  return (
                    <tr key={i}>
                      <td style={{fontSize:11,color:"#64748b",whiteSpace:"nowrap"}}>
                        {l.timestamp_str || (l.created_at ? fmtDate(l.created_at) : "—")}
                      </td>
                      <td style={{fontWeight:600,fontSize:12}}>{l.user_name||"—"}</td>
                      <td>
                        {l.role && (
                          <span style={{padding:"2px 7px",borderRadius:8,fontSize:10,
                            fontWeight:700,background:"#f1f5f9",color:"#334155"}}>
                            {l.role.toUpperCase()}
                          </span>
                        )}
                      </td>
                      <td>
                        <span style={{padding:"2px 8px",borderRadius:8,fontSize:10,
                          fontWeight:700,background:ac.bg,color:ac.color}}>
                          {l.action||"—"}
                        </span>
                      </td>
                      <td style={{fontSize:11,color:"#374151",maxWidth:340,
                        overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>
                        {l.detail||"—"}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}

export default AuditPg;
